import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (sectionId === '#contacto' && location.pathname !== '/') {
      navigate('/contacto');
      return;
    }
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-obsidian/70 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_automate-luxury/artifacts/layrdpmr_domodomotics.png" 
              alt="Domotics Logo" 
              className="h-16 w-auto"
            />
          </Link>
        </div>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center gap-8">
          <button 
            className="text-gray-300 hover:text-brand-light transition-colors text-sm uppercase tracking-wider font-heading font-light"
          >
            {t('nav.inicio')}
          </button>
          <Link 
            to="/servicios"
            className="text-gray-300 hover:text-brand-light transition-colors text-sm uppercase tracking-wider font-heading font-light"
          >
            {t('nav.servicios')}
          </Link>
          <Link 
            to="/proyectos"
            className="text-gray-300 hover:text-brand-light transition-colors text-sm uppercase tracking-wider font-heading font-light"
          >
            {t('nav.proyectos')}
          </Link>
          <Link 
            to="/nosotros"
            className="text-gray-300 hover:text-brand-light transition-colors text-sm uppercase tracking-wider font-heading font-light"
          >
            {t('nav.nosotros')}
          </Link>
          <button 
            onClick={() => scrollToSection('#contacto')}
            className="btn-primary px-6 py-2.5 rounded-sm transition-all text-sm uppercase tracking-wider font-medium"
          >
            {t('nav.contacto')}
          </button>
          
          {/* Language Selector */}
          <div className="flex items-center gap-1 ml-4 border-l border-white/20 pl-4">
            <button
              onClick={() => setLanguage('es')}
              className={`text-sm font-medium transition-colors ${
                language === 'es' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              ES
            </button>
            <span className="text-gray-500">|</span>
            <button
              onClick={() => setLanguage('en')}
              className={`text-sm font-medium transition-colors ${
                language === 'en' ? 'text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              EN
            </button>
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden text-white hover:text-blue-400 transition-colors"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 bg-black/98 backdrop-blur-lg transition-transform duration-500 ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: '80px' }}
      >
        <div className="container mx-auto px-6 py-12">
          <nav className="space-y-6">
            <button 
              onClick={() => scrollToSection('#inicio')}
              className="block text-2xl text-white hover:text-blue-400 transition-colors w-full text-left"
            >
              {t('nav.inicio')}
            </button>
            <Link 
              to="/servicios"
              onClick={() => setIsMenuOpen(false)}
              className="block text-2xl text-white hover:text-blue-400 transition-colors"
            >
              {t('nav.servicios')}
            </Link>
            <Link 
              to="/proyectos"
              onClick={() => setIsMenuOpen(false)}
              className="block text-2xl text-white hover:text-blue-400 transition-colors"
            >
              {t('nav.proyectos')}
            </Link>
            <Link 
              to="/nosotros"
              onClick={() => setIsMenuOpen(false)}
              className="block text-2xl text-white hover:text-blue-400 transition-colors"
            >
              {t('nav.nosotros')}
            </Link>
            <button 
              onClick={() => scrollToSection('#contacto')}
              className="block text-2xl text-white hover:text-blue-400 transition-colors w-full text-left"
            >
              {t('nav.contacto')}
            </button>
            
            {/* Mobile Language Selector */}
            <div className="flex items-center gap-4 pt-6 border-t border-white/20">
              <button
                onClick={() => setLanguage('es')}
                className={`text-xl font-medium transition-colors ${
                  language === 'es' ? 'text-blue-400' : 'text-gray-500'
                }`}
              >
                Español
              </button>
              <span className="text-gray-500">|</span>
              <button
                onClick={() => setLanguage('en')}
                className={`text-xl font-medium transition-colors ${
                  language === 'en' ? 'text-blue-400' : 'text-gray-500'
                }`}
              >
                English
              </button>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
